import { ApiResponse } from '../types';
import {
  InternalFulfilmentDeliveryResponse,
  FulfilmentOptionValidationResponse,
  ShipCarrierFulfilmentOptionValidationRequest,
} from './types';
import { ApiClient } from '../client/ApiClient';
import { generateRecaptchaHeaders } from '../utils/recaptcha/generateRecaptchaHeaders';
import { waitForAndGenerateRecaptchaToken } from '../utils/recaptcha/waitForAndGenerateRecaptchaToken';
import { getShopifyAuthHeader, ShopifyTokenGenerator } from '../utils/shopifySessionTokens';

const CARRIER_SERVICE_RECAPTCHA_ACTION = 'carrierServiceApi';

export class CarrierServiceClient extends ApiClient {
  private readonly _shipCarrierServiceBaseUrl: string;
  private readonly _shipCarrierValidateFulfilmentOptionUrl: string;
  private readonly _shopifyTokenGenerator?: ShopifyTokenGenerator;

  constructor({
    shipCarrierServiceBaseUrl,
    shipCarrierValidateFulfilmentOptionUrl = '/ValidateDeliveryOption',
    shopifyTokenGenerator,
  }: {
    shipCarrierServiceBaseUrl: string;
    shipCarrierValidateFulfilmentOptionUrl?: string;
    shopifyTokenGenerator?: ShopifyTokenGenerator;
  }) {
    super();
    this._shipCarrierServiceBaseUrl = shipCarrierServiceBaseUrl;
    this._shipCarrierValidateFulfilmentOptionUrl = shipCarrierValidateFulfilmentOptionUrl;
    this._shopifyTokenGenerator = shopifyTokenGenerator;
  }

  generateHeaders = async () => {
    const isInCheckoutExtensibility = this._shopifyTokenGenerator !== undefined;
    let userValidationHeaders: Record<string, string> = {};
    if (isInCheckoutExtensibility) {
      userValidationHeaders = (await getShopifyAuthHeader(this._shopifyTokenGenerator))!;
    } else {
      const { token, message } = await waitForAndGenerateRecaptchaToken(CARRIER_SERVICE_RECAPTCHA_ACTION);
      if (!token) {
        return { headers: null, error: message };
      }
      userValidationHeaders = generateRecaptchaHeaders(token, CARRIER_SERVICE_RECAPTCHA_ACTION);
    }
    return {
      headers: userValidationHeaders,
      error: null,
    };
  };

  validateFulfilmentOption = async (
    body: ShipCarrierFulfilmentOptionValidationRequest
  ): Promise<ApiResponse<FulfilmentOptionValidationResponse>> => {
    const { headers: requestHeaders, error: headersError } = await this.generateHeaders();
    if (headersError || !requestHeaders) {
      return { statusCode: 500, error: new Error(headersError) };
    }

    const { data, statusCode, error } = await this._fetch<InternalFulfilmentDeliveryResponse>({
      path: `${this._shipCarrierServiceBaseUrl}${this._shipCarrierValidateFulfilmentOptionUrl}`,
      method: 'POST',
      body,
      headers: requestHeaders,
    });

    if (error) {
      return { statusCode, error };
    }

    if (!data) {
      return { statusCode, error: new Error('Carrier service did not return valid fulfilment option validation data') };
    }

    const processedData: FulfilmentOptionValidationResponse = data as any;

    return { data: processedData, statusCode };
  };
}
